/* eslint-disable lingui/no-unlocalized-strings, lingui/text-restrictions */
import { useInView, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Sparkles } from "lucide-react";
import { type CSSProperties, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { ctaDestination, trackEvent } from "@/client/analytics/track";

import { type SceneId, sceneLabels, type Side, type Tool } from "./content";
import { SCENES } from "./scenes";

// How long each tool's scene plays before the next one starts.
const SCENE_MS = 6500;

export type PanelData = {
  side: Side;
  label: string;
  promise: string;
  brand: string;
  cta: string;
  tools: Tool[];
};

type PanelProps = {
  data: PanelData;
  /** Where each tool goes. */
  hrefFor: (tool: Tool) => string;
  ctaHref: string;
  /** Text link under the CTA, e.g. "See all job seeker tools". */
  more?: { label: string; to: string };
  lastVisited: boolean;
  onChoose: (side: Side) => void;
};

const isExternal = (href: string) => /^https?:\/\//.test(href);

/** One side of the fork: promise, a live scene for the selected tool, the tool index and the exit. */
export const Panel = ({ data, hrefFor, ctaHref, more, lastVisited, onChoose }: PanelProps) => {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { amount: 0.35 });
  const [active, setActive] = useState(0);
  const [engaged, setEngaged] = useState(false);

  const paused = engaged || !inView || Boolean(reduced);

  // Advance through the tools on a timer; hovering or focusing the panel takes over.
  useEffect(() => {
    if (paused) return;
    const timer = window.setTimeout(() => setActive((i) => (i + 1) % data.tools.length), SCENE_MS);
    return () => window.clearTimeout(timer);
  }, [active, paused, data.tools.length]);

  const tool = data.tools[active];
  const Scene = SCENES[tool.scene as SceneId];
  const headingId = `hub-${data.side}-title`;

  const click = (id: string, href: string) => {
    onChoose(data.side);
    trackEvent("cta_clicked", {
      cta_id: `hub_${data.side}_${id}`,
      cta_location: "hub_panel",
      destination: ctaDestination(href),
    });
  };

  return (
    <section
      ref={ref}
      className={`hub-panel hub-panel--${data.side}`}
      aria-labelledby={headingId}
      onMouseEnter={() => setEngaged(true)}
      onMouseLeave={() => setEngaged(false)}
      onFocus={() => setEngaged(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setEngaged(false);
      }}
    >
      <div className="hub-panel__top">
        <p className="hub-panel__label">
          <span className="hub-dot" aria-hidden="true" />
          {data.label}
        </p>
        {lastVisited && <p className="hub-panel__last">You were here last time</p>}
      </div>
      <h3 id={headingId} className="hub-panel__promise">
        {data.promise}
      </h3>

      <div className="hub-stage">
        <div className="hub-stage__bar">
          <span>
            <Sparkles size={12} aria-hidden="true" /> {sceneLabels[tool.scene as SceneId]}
          </span>
          <span>Illustrative</span>
        </div>
        <div
          key={tool.id}
          className="hub-stage__scene"
          role="img"
          aria-label={`${tool.name}: ${sceneLabels[tool.scene as SceneId]}. Illustrative example.`}
        >
          <Scene />
        </div>
      </div>

      <ol className="hub-tools">
        {data.tools.map((item, index) => {
          const href = hrefFor(item);
          const external = isExternal(href);
          const body = (
            <>
              <span className="hub-tool__num" aria-hidden="true">
                0{index + 1}
              </span>
              <span className="hub-tool__text">
                <strong>{item.name}</strong>
                <small>{item.benefit}</small>
              </span>
              <span className="hub-tool__dest">
                {item.lives === "brand" ? data.brand : "theResume"}
                {item.lives === "brand" ? (
                  <ArrowUpRight size={14} aria-hidden="true" />
                ) : (
                  <ArrowRight size={14} aria-hidden="true" />
                )}
              </span>
              {active === index && !paused && (
                <span
                  key={`p-${active}`}
                  className="hub-tool__progress"
                  aria-hidden="true"
                  style={{ "--ms": `${SCENE_MS}ms` } as CSSProperties}
                />
              )}
            </>
          );
          const props = {
            className: "hub-tool",
            "data-active": active === index,
            onMouseEnter: () => setActive(index),
            onFocus: () => setActive(index),
            onClick: () => click(item.id, href),
          };
          return (
            <li key={item.id}>
              {external ? (
                <a href={href} {...props}>
                  {body}
                </a>
              ) : (
                <Link to={href} {...props}>
                  {body}
                </Link>
              )}
            </li>
          );
        })}
      </ol>

      <div className="hub-panel__foot">
        <a href={ctaHref} className="hub-cta" onClick={() => click("primary", ctaHref)}>
          {data.cta} <ArrowUpRight size={18} aria-hidden="true" />
        </a>
        {more && (
          <Link to={more.to} className="hub-more" onClick={() => click("more", more.to)}>
            {more.label} <ArrowRight size={14} aria-hidden="true" />
          </Link>
        )}
      </div>
    </section>
  );
};
